import { cloneDeep } from "lodash";

import { firestore, MODEL_COLL } from "_firebase";
import { DashboardAPI } from "utils/api";
import { defaultUtilityCosts } from "utils/utilityData/defaultCosts";

export const ACCESSOR = "model.insightsData.hybridAnalysis";

// Month and hour keys, matching the preset schedules and the schedule grid.
// Firestore cannot store an array inside an array, so this never becomes one.
const emptySchedule = () => {
    const schedule = {};
    for (let month = 0; month < 12; month += 1) {
        schedule[month] = {};
        for (let hour = 0; hour < 24; hour += 1) {
            schedule[month][hour] = 0;
        }
    }
    return schedule;
};

export const defaultTimeOfUse = () => ({
    presetTOU: "User Defined",
    numberOfPeriods: 1,
    periodPrices: [0],
    timeOfUseLabels: ["Rate 1"],
    weekdayScheduleTOU: emptySchedule(),
    weekendScheduleTOU: emptySchedule(),
});

export const buildHybridAnalysis = ({ saved, fuelPrices, timeOfUse } = {}) => {
    const hasFuelPrices = fuelPrices && Object.keys(fuelPrices).length > 0;
    const hasTimeOfUse = timeOfUse && (timeOfUse.presetTOU || timeOfUse.numberOfPeriods);

    const seeded = {
        upgradePackageId: "base",
        electricityRateType: "flat",
        fuelPrices: cloneDeep(hasFuelPrices ? fuelPrices : defaultUtilityCosts),
        timeOfUse: cloneDeep(hasTimeOfUse ? timeOfUse : defaultTimeOfUse()),
        capitalCosts: { heatPump: 0, furnace: 0 },
        lifetimeYears: 15,
    };

    if (!saved) return seeded;

    return {
        ...seeded,
        ...saved,
        fuelPrices: saved.fuelPrices || seeded.fuelPrices,
        timeOfUse: saved.timeOfUse || seeded.timeOfUse,
        capitalCosts: { ...seeded.capitalCosts, ...(saved.capitalCosts || {}) },
    };
};

const hybridFeatureDoc = (modelId) =>
    firestore.doc(`${MODEL_COLL}/${modelId}/modelData/insightsData/features/hybridHeating`);

const hybridRunDoc = (modelId, runId) =>
    firestore.doc(`${MODEL_COLL}/${modelId}/modelData/insightsData/features/hybridHeating/runs/${runId}`);

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const scheduleCell = (schedule, month, hour) => {
    const period = Number(schedule?.[month]?.[hour]);
    return Number.isFinite(period) ? period : 0;
};

const periodPriceDollars = (periodPrices, period) => {
    if (!period) return 0;
    const raw = Array.isArray(periodPrices) ? periodPrices[period - 1] : periodPrices?.[`period${period}`];
    const cents = Number(raw);
    if (!Number.isFinite(cents)) return 0;
    return parseFloat(cents.toFixed(3)) / 100;
};

// Equal weight for every hour of a non-leap year. Saturday and Sunday use the weekend schedule.
export const averageHourlyElectricityRate = (timeOfUse = {}) => {
    const { periodPrices, weekdayScheduleTOU, weekendScheduleTOU } = timeOfUse;
    let total = 0;
    let weekday = 0;

    DAYS_IN_MONTH.forEach((days, month) => {
        for (let day = 0; day < days; day += 1) {
            const schedule = weekday === 0 || weekday === 6 ? weekendScheduleTOU : weekdayScheduleTOU;
            for (let hour = 0; hour < 24; hour += 1) {
                total += periodPriceDollars(periodPrices, scheduleCell(schedule, month, hour));
            }
            weekday = (weekday + 1) % 7;
        }
    });

    return Math.round((total / 8760) * 1000) / 1000;
};

export const loadInsightsData = async (modelId) => {
    const snapshot = await hybridFeatureDoc(modelId).get();
    return snapshot.exists ? snapshot.data() : null;
};

export const saveHybridAnalysis = (modelId, hybridAnalysis) =>
    hybridFeatureDoc(modelId).set(
        {
            config: hybridAnalysis,
            updatedAt: new Date().toISOString(),
        },
        { merge: true }
    );

const publishHybridRun = (snapshot, onRun) => {
    if (!snapshot.exists) {
        onRun(null);
        return;
    }

    const run = snapshot.data() || {};
    onRun({
        id: snapshot.id,
        status: run.status || "queued",
        upgradePackageId: run.upgradePackageId || "base",
        backupFuelKey: run.backupFuelKey || "",
        completedTemperatures: Array.isArray(run.completedTemperatures) ? run.completedTemperatures : [],
        switchoverTemperatures: Array.isArray(run.switchoverTemperatures) ? run.switchoverTemperatures : [],
        refinements: Array.isArray(run.refinements) ? run.refinements : [],
        error: run.error || "",
    });
};

export const startHybridHeatingAnalysis = async ({ modelId, expVersion, upgradePackageId }) => {
    const response = await DashboardAPI({
        method: "POST",
        url: "/runHybridHeatingAnalysis",
        data: {
            modelId,
            expVersion,
            upgradePackageId: upgradePackageId || "base",
        },
    });

    if (response?.status === 202 && response?.data?.runId) {
        return { ok: true, runId: response.data.runId };
    }

    const message =
        response?.response?.data?.message || response?.data?.message || "Hybrid heating analysis could not be started.";
    return { ok: false, message };
};

export const startHybridHeatingRefinement = async ({ modelId, runId, fromC, toC }) => {
    const response = await DashboardAPI({
        method: "POST",
        url: "/runHybridHeatingRefinement",
        data: { modelId, runId, fromC, toC },
    });

    if (response?.status === 202 && response?.data?.runId) {
        return { ok: true, ...response.data };
    }

    const message =
        response?.response?.data?.message || response?.data?.message || "Hybrid heating refinement could not be started.";
    return { ok: false, message };
};

export const retryHybridHeatingRefinement = async ({ modelId, runId, fromC, toC }) => {
    const response = await DashboardAPI({
        method: "POST",
        url: "/runHybridHeatingRefinementRetry",
        data: { modelId, runId, fromC, toC },
    });

    if (response?.status === 202 && response?.data?.runId) {
        return { ok: true, ...response.data };
    }

    const message =
        response?.response?.data?.message ||
        response?.data?.message ||
        "Hybrid heating refinement could not be retried.";
    return { ok: false, message };
};

export const subscribeHybridRun = (modelId, onRun) => {
    let unsubscribeRun = () => {};
    let activeRunId = null;

    const unsubscribeFeature = hybridFeatureDoc(modelId).onSnapshot(
        (snapshot) => {
            const currentRunId = snapshot.data()?.currentRunId || null;
            if (!currentRunId) {
                activeRunId = null;
                unsubscribeRun();
                unsubscribeRun = () => {};
                onRun(null);
                return;
            }
            if (currentRunId === activeRunId) return;

            activeRunId = currentRunId;
            unsubscribeRun();
            unsubscribeRun = hybridRunDoc(modelId, currentRunId).onSnapshot(
                (runSnapshot) => publishHybridRun(runSnapshot, onRun),
                (error) => {
                    console.log(error);
                    onRun({ status: "listen-error", error: "Analysis progress could not be read." });
                }
            );
        },
        (error) => {
            console.log(error);
            onRun({ status: "listen-error", error: "Analysis progress could not be read." });
        }
    );

    return () => {
        unsubscribeRun();
        unsubscribeFeature();
    };
};

export const subscribeHybridResults = (modelId, runId, onResults) => {
    if (!modelId || !runId) return () => {};

    return hybridRunDoc(modelId, runId)
        .collection("results")
        .onSnapshot(
            (snapshot) => {
                const results = {};
                snapshot.forEach((doc) => {
                    results[doc.id] = doc.data();
                });
                onResults(results);
            },
            (error) => {
                console.log(error);
                onResults(null);
            }
        );
};
